import { Col, Form, Row, InputGroup } from 'react-bootstrap';
import Select, { components, ControlProps, OptionTypeBase, Props, Styles } from 'react-select';
import React, { forwardRef, MutableRefObject, ReactElement, ReactNode } from 'react';
import CreatableSelect, { CreatableProps } from 'react-select/creatable';
import { StylesConfigFunction, StylesConfig } from 'react-select/src/styles';

export type SelectOption = {
    value: string | number;
    label: ReactNode;
};

export type PropsDAutocompletion<T extends OptionTypeBase> = Props<T> &
    CreatableProps<T, false> & {
        id?: string;
        titre?: ReactNode;
        enErreur?: boolean;
        erreur?: string;
        children?: ReactNode;
        cacherTitre?: boolean;
        isCreatable?: boolean;
        styles?: Partial<Styles<T, false>> & { formGroup: StylesConfigFunction };
        avecUnBouton?: boolean;
        placeholder?: string;
        labelCol?: number;
        estPetit?: boolean;
        appendedChildren?: ReactElement;
        disabled?: boolean;
        onCreateOption?: (inputValue: string) => void;
    };

type IsMulti = false;

const ControlCustom = function <T extends OptionTypeBase>(ctrlProps: ControlProps<T, false>) {
    const {
        selectProps: { enErreur },
    } = ctrlProps;
    return (
        <components.Control {...ctrlProps} className={'form-control ' + (enErreur ? 'py-0 pl-0 is-invalid' : 'p-0')} />
    );
};

/**
 * Crée le css nécessaire pour les champs avec un menu deroulant. Le CSS est supposé
 * faire en sorte que les <Select> et les <Creatable> ressemblent aux <Form.Control size='sm'>
 * @returns CSS pour les elements HTML react-select
 */
function getStyles<T extends OptionTypeBase>(): StylesConfig<T, IsMulti> {
    return {
        control: (provided) => ({
            ...provided,
            minHeight: '31px',
            height: '31px',
            borderRadius: '0.2rem',
            border: '1px solid #ced4da',
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: '31px',
            padding: '0 6px',
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',
            fontSize: '0.875em',
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
            paddingBottom: '2px',
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
            paddingBottom: '2px',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '31px',
        }),
    };
}

/**
 * Retourne un champ avec un menu deroulant quand on appuie sur la petite fleche
 * Si on a un labelCol, c'est qu'on est dans le dashboard et on veut une seule
 * ligne avec le titre et le champ. On met alors aussi un champ plus compact (petit)
 * @param props
 * @param ref
 * @returns HTML d'un champ avec un menu déroulant
 */
function ChampAutocompletionAvecRef<T extends OptionTypeBase>(props: PropsDAutocompletion<T>, ref): ReactElement {
    const {
        id,
        titre,
        children,
        enErreur,
        erreur,
        cacherTitre,
        isCreatable,
        avecUnBouton,
        placeholder,
        labelCol,
        estPetit,
        appendedChildren,
        disabled,
        onCreateOption,
        ...rest
    } = props;
    const selectProps = {
        placeholder: placeholder ? placeholder : 'Chercher ou créer...',
        loadingMessage: () => 'Chargement...',
        noOptionsMessage: () => 'Aucun résultat',
        formatCreateLabel: (option: string) => `Créer "${option}"`,
        classNamePrefix: 'react-select',
        className: 'flex-grow-1',
        components: { Control: ControlCustom },
        enErreur: enErreur,
        ...rest,
    };
    const styles = getStyles<T>();

    /**
     * Etiquette qui identifie un champ avec menu deroulant
     * @returns un label html
     */
    function etiquette(): JSX.Element {
        if (cacherTitre) return <div className={enErreur ? 'is-invalid' : ''} />;

        return <Form.Label className={enErreur ? 'is-invalid' : ''}>{titre}</Form.Label>;
    }

    /**
     * Champ avec menu deroulant. Un creatable permet d'ecrire son option custom. Un
     * select oblige un choix existant. avecUnBouton est true lorsque le bouton pour
     * ouvir le menu deroulant est a l'exterieur du champ
     * @returns HTML d'un champ avec un menu déroulant
     */
    function champ(petit: Boolean, disabled: boolean | undefined): JSX.Element {
        let champ: ReactElement;
        if (!isCreatable) {
            champ = (
                <Select
                    ref={ref as MutableRefObject<Select<T>>}
                    {...selectProps}
                    placeholder="Rechercher..."
                    styles={petit ? styles : undefined}
                    isDisabled={disabled}
                />
            );
        } else if (avecUnBouton) {
            champ = (
                <CreatableSelect
                    isClearable
                    ref={ref as MutableRefObject<CreatableSelect<T>>}
                    onCreateOption={onCreateOption}
                    styles={petit ? styles : undefined}
                    {...selectProps}
                />
            );
        } else {
            champ = (
                <CreatableSelect
                    isClearable
                    ref={ref as MutableRefObject<CreatableSelect<T>>}
                    onCreateOption={onCreateOption}
                    styles={petit ? styles : undefined}
                    isDisabled={disabled}
                    {...selectProps}
                />
            );
        }

        return appendedChildren ? (
            <InputGroup>
                {champ}
                <InputGroup.Append>{appendedChildren}</InputGroup.Append>
            </InputGroup>
        ) : (
            champ
        );
    }

    if (labelCol) {
        const cssEtiquette: React.CSSProperties = {
            display: cacherTitre ? 'none' : 'block',
        };

        return (
            <Form.Group controlId={id} style={{ ...rest.styles?.formGroup({}, {}), ...rest.style }}>
                <Row>
                    <Col xs={labelCol} style={cssEtiquette}>
                        {etiquette()}
                    </Col>
                    <Col style={{ width: '300px' }}>{champ(!!estPetit, disabled)}</Col>
                </Row>
                <Form.Control.Feedback type="invalid">{erreur}</Form.Control.Feedback>
                {children}
            </Form.Group>
        );
    }

    return (
        <Form.Group controlId={id} style={rest.styles?.formGroup({}, {})}>
            {etiquette()}
            {champ(!!estPetit, disabled)}
            <Form.Control.Feedback type="invalid">{erreur}</Form.Control.Feedback>
            {children}
        </Form.Group>
    );
}

const ChampAutocompletion = forwardRef(ChampAutocompletionAvecRef);

export default ChampAutocompletion;
